import type { MouseEvent } from "react";
import { type AppointmentRowPill, appointmentRowPillDescription } from "./appointmentRowState";

/**
 * A booking row's one pill (appointmentRowState): "Intake to review", "Health answers to review",
 * "Intake reviewed", "Chart to link", and for a text or email "Text not delivered", "Check phone
 * number" or "Texts stopped" (PA-7)... Role-aware because the server sends each member only the
 * questionnaire sections they review.
 *
 * - `surface="schedule"`: `.schedule-intake-badge` (schedule.css), also on compact calendar
 *   cards, where it reads `shortLabel`.
 * - `surface="today"`: `.tb-intake-pill` (today-board.css) on Today's rows and the Lobby.
 *
 * With `onOpen` it is a button (never inside another button: Schedule's cards and agenda rows
 * are buttons, so they pass none and open the review from the side panel).
 */
export function IntakeReviewPill({
  pill,
  patientName,
  surface,
  compact = false,
  onOpen,
}: {
  readonly pill: AppointmentRowPill;
  readonly patientName: string;
  readonly surface: "schedule" | "today";
  readonly compact?: boolean;
  readonly onOpen?: (() => void) | undefined;
}) {
  const className =
    surface === "today"
      ? `tb-intake-pill tone-${pill.tone}`
      : `schedule-intake-badge tone-${pill.tone}${compact ? " compact" : ""}`;
  const text = compact ? pill.shortLabel : pill.label;
  if (onOpen === undefined) {
    return (
      <span className={className} data-pill-kind={pill.kind}>
        {text}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={className}
      data-pill-kind={pill.kind}
      aria-label={appointmentRowPillDescription(pill, patientName)}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        // Rows select or expand on click; the pill opens only the review.
        event.stopPropagation();
        onOpen();
      }}
    >
      {text}
    </button>
  );
}
